/**
 * The firm's logo — one control, in two places (C12, ADR-0030 §2, T-1.26).
 *
 * *Your firm → Identity* and *Site → Theme* both draw this, and both write the
 * one field there is: `prodesk_firm_profile.logo_url`. The site, its
 * `/site-brand.json` and the client portal all draw what is chosen here.
 *
 * A picture of the current logo and **two** actions — C24 item 9:
 *
 * * **Choose a picture** — one `PicturePicker` (ADR-0047 §2), with the firm's
 *   Pictures (upload and drop inside it, ADR-0021's limits checked there and
 *   again by the service), C24's shipped graphics, a **Logos we ship** tab
 *   carrying ADR-0030's four with the firm's own profession first, and a link.
 *   A profession with no default of its own — `other` (*"for others they will
 *   upload their logos"*) — is told so rather than offered another
 *   profession's mark;
 * * **Remove** — confirmed, because it changes three public surfaces at once.
 *
 * Until C24 those were four buttons: *Upload*, *Choose from Pictures*, *Use a
 * default* and *Remove*, plus a `DefaultLogoDialog` of its own which has gone
 * — the *Logos we ship* tab is what it became. This control still exists
 * rather than being replaced by the picker outright, because a logo is not an
 * ordinary picture: it has *Remove*, it has the shipped marks, it has its own
 * sizing advice, and **every action here saves at once**.
 *
 * **Every action saves at once**, through `onChange`. A logo is one deliberate
 * choice rather than a field typed into, and the two screens it sits on save
 * differently — a section Save on *Your firm*, *Save to draft* on Theme — so a
 * control that waited for either would be a logo that is chosen on one screen
 * and unsaved on the other. A typed URL is no longer offered (ADR-0030 §2); a
 * firm that typed one before Wave 13 still sees it here and can replace it.
 */

import { Alert, Badge, Button, ConfirmDialog } from '@fiveox/ui'
import { ImageIcon, Images, Loader2, Trash2 } from 'lucide-react'
import { useState } from 'react'

import { PicturePicker, type PickedPicture } from './site/PicturePicker'
import { ApiError } from '../lib/api'
import { defaultLogoOf, logoSource } from '../lib/defaultLogos'

export interface LogoControlProps {
  /** The stored logo, or `null` for none. */
  value: string | null
  /** The firm's profession — orders the defaults, and `other` has none. */
  profession: string | null
  /** May change the logo at all (the screen's own update verb). */
  editable: boolean
  /** `site:can_view` — the Pictures library can be browsed. */
  canBrowse: boolean
  /** `site:can_update` — a picture can be uploaded into the library. */
  canUpload: boolean
  /** Writes the one field. `null` removes the logo. Rejects with the refusal. */
  onChange: (next: string | null) => Promise<void>
}

const SOURCE_LINE: Record<ReturnType<typeof logoSource>, string> = {
  none: 'No logo yet. Your website, client portal and sign-in page show your firm’s name alone.',
  default: 'A default logo.',
  library: 'From your Pictures.',
  link: 'From a web address set before logos moved here. Replace it with an upload to keep it on your own site.',
}

function refusal(err: unknown, fallback: string): string {
  return err instanceof ApiError ? err.message : fallback
}

export function LogoControl({
  value,
  profession,
  editable,
  canBrowse,
  canUpload,
  onChange,
}: LogoControlProps) {
  const [busy, setBusy] = useState<null | 'save' | 'remove'>(null)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  const [broken, setBroken] = useState<string | null>(null)
  const [pickerOpen, setPickerOpen] = useState(false)
  const [removeOpen, setRemoveOpen] = useState(false)

  const source = logoSource(value)
  const chosenDefault = defaultLogoOf(value)

  async function apply(next: string | null, kind: 'save' | 'remove' = 'save') {
    setBusy(kind)
    setError(null)
    setSaved(false)
    try {
      await onChange(next)
      setBroken(null)
      setSaved(true)
      return true
    } catch (err) {
      setError(refusal(err, 'The logo could not be saved.'))
      return false
    } finally {
      setBusy(null)
    }
  }

  /**
   * Whatever the window handed back, saved at once.
   *
   * **Dimensions are dropped on purpose.** `prodesk_firm_profile.logo_url` is
   * one column and there is nowhere to put them; a logo is drawn at a height
   * the theme chooses, not at its own, so there is no box to reserve.
   */
  function picked(picture: PickedPicture) {
    void apply(picture.url)
  }

  const pictureBroken = Boolean(value) && broken === value

  return (
    <div role="group" aria-label="Logo" className="space-y-3" data-logo-control>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
        <div
          className="border-border bg-muted/40 flex h-28 w-28 shrink-0 items-center justify-center overflow-hidden rounded-2xl border"
          data-logo-preview
        >
          {value && !pictureBroken ? (
            <img
              src={value}
              alt="Your logo"
              className="h-full w-full object-contain p-2"
              onError={() => setBroken(value)}
            />
          ) : (
            <span className="text-muted-foreground flex flex-col items-center gap-1 text-xs">
              <ImageIcon size={24} aria-hidden />
              {pictureBroken ? 'Not found' : 'No logo'}
            </span>
          )}
        </div>

        <div className="min-w-0 flex-1 space-y-3">
          <div>
            <p className="text-foreground text-sm font-medium">
              {chosenDefault ? chosenDefault.name : source === 'none' ? 'Logo' : 'Your logo'}
              {chosenDefault && (
                <Badge tone="info" size="sm" className="ml-2 align-middle">
                  Default
                </Badge>
              )}
            </p>
            <p className="text-muted-foreground text-sm" data-logo-source={source}>
              {pictureBroken
                ? 'This picture could not be loaded. Choose another, or remove it.'
                : SOURCE_LINE[source]}
            </p>
          </div>

          {editable ? (
            <div className="flex flex-wrap gap-2">
              {/* C24 item 9: one button for every way a logo can arrive. */}
              <Button
                size="sm"
                variant={value ? 'outline' : 'primary'}
                disabled={busy !== null}
                onClick={() => setPickerOpen(true)}
              >
                {busy === 'save' ? (
                  <Loader2 size={14} className="animate-spin" aria-hidden />
                ) : (
                  <Images size={14} aria-hidden />
                )}
                {busy === 'save' ? 'Saving…' : value ? 'Change your logo' : 'Choose a logo'}
              </Button>
              {value && (
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={busy !== null}
                  onClick={() => setRemoveOpen(true)}
                >
                  <Trash2 size={14} aria-hidden /> Remove
                </Button>
              )}
            </div>
          ) : (
            <p className="text-muted-foreground text-xs">You can see the logo but not change it.</p>
          )}

          {editable && (
            <p className="text-muted-foreground text-xs">
              JPEG, PNG or WebP, up to 5 MB. A square or wide logo on a plain or transparent
              background works best.
              {!canBrowse && ' Uploading and choosing from Pictures need access to the website builder.'}
            </p>
          )}
        </div>
      </div>

      {error && <Alert tone="error">{error}</Alert>}
      {saved && !error && (
        <p role="status" className="text-primary text-sm font-medium">
          Logo saved. Your website, portal and sign-in page use it now.
        </p>
      )}

      <PicturePicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={picked}
        editable={canUpload}
        logos={{ profession }}
        value={value}
        title="Choose your logo"
      />

      <ConfirmDialog
        open={removeOpen}
        onClose={() => setRemoveOpen(false)}
        title="Remove your logo?"
        confirmLabel="Remove logo"
        cancelLabel="Keep it"
        tone="danger"
        busy={busy === 'remove'}
        busyLabel="Removing…"
        onConfirm={async () => {
          if (await apply(null, 'remove')) setRemoveOpen(false)
        }}
      >
        <p className="text-muted-foreground text-sm">
          Your website, client portal and sign-in page will show your firm’s name alone. An
          uploaded picture stays in your Pictures.
        </p>
      </ConfirmDialog>
    </div>
  )
}
